import { useState, useCallback } from 'react';
import SEED_PRODUCTS from '../data/products';

const STORAGE_KEY = 'cottoncubs_products';

function loadProducts() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      if (Array.isArray(parsed) && parsed.length > 0) return parsed;
    }
  } catch (e) { /* ignore corrupt data */ }
  const seeded = SEED_PRODUCTS.map(p => ({
    ...p,
    status: 'active',
    stock: 99,
    trackInventory: false,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  }));
  localStorage.setItem(STORAGE_KEY, JSON.stringify(seeded));
  return seeded;
}

export default function useProducts() {
  const [products, setProducts] = useState(loadProducts);

  const persist = useCallback((updated) => {
    setProducts(updated);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
  }, []);

  const getProducts = useCallback(({ search = '', status = 'all', category = 'all', sortBy = 'name', sortDir = 'asc' } = {}) => {
    let filtered = [...products];
    if (status !== 'all') filtered = filtered.filter(p => p.status === status);
    if (category !== 'all') filtered = filtered.filter(p => p.category === category);
    if (search) {
      const q = search.toLowerCase();
      filtered = filtered.filter(p => p.name.toLowerCase().includes(q) || p.subtitle.toLowerCase().includes(q));
    }
    filtered.sort((a, b) => {
      let cmp = 0;
      if (sortBy === 'name') cmp = a.name.localeCompare(b.name);
      else if (sortBy === 'price') cmp = a.price - b.price;
      else if (sortBy === 'stock') cmp = (a.stock || 0) - (b.stock || 0);
      else if (sortBy === 'status') cmp = (a.status || '').localeCompare(b.status || '');
      else if (sortBy === 'category') cmp = a.category.localeCompare(b.category);
      return sortDir === 'desc' ? -cmp : cmp;
    });
    return filtered;
  }, [products]);

  const getProduct = useCallback((id) => products.find(p => p.id === id), [products]);

  const createProduct = useCallback((data) => {
    const maxId = products.reduce((max, p) => Math.max(max, p.id), 0);
    const now = new Date().toISOString();
    const newProduct = {
      ...data,
      id: maxId + 1,
      status: data.status || 'draft',
      stock: data.stock ?? 0,
      trackInventory: data.trackInventory ?? false,
      createdAt: now,
      updatedAt: now,
    };
    persist([...products, newProduct]);
    return newProduct;
  }, [products, persist]);

  const updateProduct = useCallback((id, data) => {
    const updated = products.map(p =>
      p.id === id ? { ...p, ...data, updatedAt: new Date().toISOString() } : p
    );
    persist(updated);
  }, [products, persist]);

  const deleteProducts = useCallback((ids) => {
    persist(products.filter(p => !ids.includes(p.id)));
  }, [products, persist]);

  const bulkUpdateStatus = useCallback((ids, status) => {
    const now = new Date().toISOString();
    const updated = products.map(p =>
      ids.includes(p.id) ? { ...p, status, updatedAt: now } : p
    );
    persist(updated);
  }, [products, persist]);

  const activeProducts = products.filter(p => p.status === 'active');

  return { products, activeProducts, getProducts, getProduct, createProduct, updateProduct, deleteProducts, bulkUpdateStatus };
}
